import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { navigateToMainPageSection } from '../../utils/scrollUtils';
import bonamixLogo from '../home/images/bonamix.jpg';
import cbfLogo from '../home/images/cbf.jpg';
import diegoLogo from '../home/images/diego.jpg';
import domaLogo from '../home/images/doma.jpg';
import soundraLogo from '../home/images/soundra.jpg';
import drevusaLogo from '../home/images/drevusa.jpg';
import './KidsSpacePage.css';

// ─── Gallery images array – add future images here ──────────────────────────
const GALLERY_IMAGES = [
  '/images/tideasoliskids/whole.jpeg',
  '/images/tideasoliskids/1.jpeg',
  '/images/tideasoliskids/2.jpeg',
  '/images/tideasoliskids/3.jpeg',
  '/images/tideasoliskids/4.jpeg',
  '/images/tideasoliskids/5.jpeg',
  '/images/tideasoliskids/6.jpeg',
  '/images/tideasoliskids/7.jpeg',
];

// ─── Kids Space partners ─────────────────────────────────────────────────────
const KIDS_PARTNERS = [
  { name: 'Bonamix',          logo: bonamixLogo, url: 'https://www.bonamix.sk/' },
  { name: 'CBF Reality',      logo: cbfLogo,     url: 'https://www.cbf-reality.sk/' },
  { name: 'Diego',            logo: diegoLogo,   url: 'https://www.diego.sk/' },
  { name: 'Nábytok DOMA',     logo: domaLogo,    url: 'https://eshop.nabytokdoma.sk/' },
  { name: 'Soundra Therapy',  logo: soundraLogo, url: 'https://www.soundratherapy.sk/' },
  { name: 'Drevuša',          logo: drevusaLogo, url: null },
];

// ─── Rules ───────────────────────────────────────────────────────────────────
const RULES = [
  { emoji: '👟', key: 'kids_space.rule_1' },
  { emoji: '🧼', key: 'kids_space.rule_2' },
  { emoji: '🧸', key: 'kids_space.rule_3' },
  { emoji: '🍎', key: 'kids_space.rule_4' },
  { emoji: '🕊️', key: 'kids_space.rule_5' },
  { emoji: '🤝', key: 'kids_space.rule_6' },
  { emoji: '🧘', key: 'kids_space.rule_7' },
  { emoji: '🤍', key: 'kids_space.rule_8' },
  { emoji: '🌿', key: 'kids_space.rule_9' },
  { emoji: '✨', key: 'kids_space.rule_10' },
];

const KidsSpacePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [whyExpanded, setWhyExpanded] = useState(false);
  const [movementExpanded, setMovementExpanded] = useState(false);
  const [rulesExpanded, setRulesExpanded] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'TiDea Solis Kids';
  }, []);

  const handleViewSchedule = () => {
    navigateToMainPageSection('schedule', 500, 120);
  };

  const openGallery = (index) => {
    setGalleryIndex(index);
    setGalleryOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeGallery = () => {
    setGalleryOpen(false);
    document.body.style.overflow = '';
  };

  const nextImage = (e) => {
    e && e.stopPropagation();
    setGalleryIndex((prev) => (prev + 1) % GALLERY_IMAGES.length);
  };

  const prevImage = (e) => {
    e && e.stopPropagation();
    setGalleryIndex((prev) => (prev - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (!galleryOpen) return;
      if (e.key === 'Escape') closeGallery();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [galleryOpen]);

  return (
    <div className="kids-page">
      {/* Decorative ellipses – same technique as WorkshopDetails */}
      <div className="kids-left-ellipse kids-deco"></div>
      <div className="kids-right-ellipse kids-deco"></div>

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <header className="kids-page-header">
        <div className="kids-page-header-inner">
          <div className="kids-header-wordmark">
            <img
              src="/images/tideasoliskids/kidslogo.jpeg"
              alt="TiDea Solis Kids"
              className="kids-header-logo"
            />
            <h1 className="kids-header-title">TiDea Solis Kids</h1>
          </div>
          <p className="kids-header-tagline">{t('kids_space.page_tagline')}</p>
        </div>
      </header>

      <div className="kids-page-body">

        {/* ── Section 1: Intro & Story ─────────────────────────────────── */}
        <section className="kids-section kids-intro-section">
          <div className="kids-split-row">
            {/* Text side */}
            <div className="kids-split-text">
              <h2 className="kids-section-title">{t('kids_space.intro_section_title')}</h2>
              <p className="kids-body-text">{t('kids_space.intro_welcome')}</p>

              {/* Expandable backstory */}
              <div className="kids-expandable">
                <h3 className="kids-sub-title">{t('kids_space.why_title')}</h3>
                <p className="kids-body-text">{t('kids_space.why_short')}</p>
                {whyExpanded && (
                  <p className="kids-body-text kids-fade-in">{t('kids_space.why_full')}</p>
                )}
                <button
                  className="kids-expand-btn"
                  onClick={() => setWhyExpanded(!whyExpanded)}
                  aria-expanded={whyExpanded}
                >
                  {whyExpanded ? t('kids_space.read_less') : t('kids_space.read_more')}
                  <span className="kids-expand-arrow">{whyExpanded ? ' ▲' : ' ▼'}</span>
                </button>
              </div>
            </div>

            {/* Image side */}
            <div className="kids-split-image">
              <div className="kids-img-tile-wrap">
                <img
                  src="/images/tideasoliskids/whole.jpeg"
                  alt="TiDea Solis Kids priestor"
                  className="kids-img-tile"
                  onClick={() => openGallery(0)}
                />
              </div>
            </div>
          </div>

          {/* Age group pills */}
          <div className="kids-for-whom">
            <h3 className="kids-sub-title kids-for-whom-heading">{t('kids_space.for_whom_title')}</h3>
            <div className="kids-age-pills">
              <div className="kids-age-pill">
                <span className="kids-age-range">0 – 3</span>
                <span className="kids-age-years">rokov</span>
                <span className="kids-age-desc">{t('kids_space.age_0_3_desc')}</span>
              </div>
              <div className="kids-age-pill">
                <span className="kids-age-range">3 – 9</span>
                <span className="kids-age-years">rokov</span>
                <span className="kids-age-desc">{t('kids_space.age_3_9_desc')}</span>
              </div>
              <div className="kids-age-pill">
                <span className="kids-age-range">10 – 15</span>
                <span className="kids-age-years">rokov</span>
                <span className="kids-age-desc">{t('kids_space.age_teens_desc')}</span>
              </div>
              <div className="kids-age-pill">
                <span className="kids-age-range">🤰</span>
                <span className="kids-age-desc">{t('kids_space.age_moms_desc')}</span>
              </div>
              <div className="kids-age-pill">
                <span className="kids-age-range">👨‍👩‍👧‍👦</span>
                <span className="kids-age-desc">{t('kids_space.age_groups_desc')}</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── Section 2: Activities ────────────────────────────────────── */}
        <section className="kids-section kids-activities-section">
          <h2 className="kids-section-title kids-section-title--centered">
            {t('kids_space.sections_title')}
          </h2>

          {/* Row A: Pohyb & Joga — text left, image right */}
          <div className="kids-activity-row kids-activity-row--normal">
            <div className="kids-activity-card">
              <div className="kids-activity-head">
                <span className="kids-activity-emoji">🧘‍♀️</span>
                <h3 className="kids-activity-title">{t('kids_space.movement_title')}</h3>
              </div>
              <p className="kids-body-text">{t('kids_space.movement_short')}</p>
              {movementExpanded && (
                <div className="kids-fade-in">
                  <p className="kids-body-text kids-indent-text">{t('kids_space.movement_psycho')}</p>
                  <p className="kids-body-text kids-indent-text">{t('kids_space.movement_kids')}</p>
                  <p className="kids-body-text kids-indent-text">{t('kids_space.movement_teens')}</p>
                  <p className="kids-body-text kids-indent-text">{t('kids_space.movement_gravid')}</p>
                </div>
              )}
              <button
                className="kids-expand-btn"
                onClick={() => setMovementExpanded(!movementExpanded)}
                aria-expanded={movementExpanded}
              >
                {movementExpanded ? t('kids_space.hide_details') : t('kids_space.show_details')}
                <span className="kids-expand-arrow">{movementExpanded ? ' ▲' : ' ▼'}</span>
              </button>
            </div>
            <div className="kids-activity-image-wrap">
              <img
                src="/images/tideasoliskids/1.jpeg"
                alt={t('kids_space.movement_title')}
                className="kids-activity-img"
                onClick={() => openGallery(1)}
              />
            </div>
          </div>

          {/* Row B: Mindfulness — full width */}
          <div className="kids-activity-row kids-activity-row--full">
            <div className="kids-activity-card kids-activity-card--wide">
              <div className="kids-activity-head">
                <span className="kids-activity-emoji">🌿</span>
                <h3 className="kids-activity-title">{t('kids_space.mindfulness_title')}</h3>
              </div>
              <p className="kids-body-text">{t('kids_space.mindfulness_text')}</p>
            </div>
          </div>

          {/* Row C: Workshopy — image left, text right */}
          <div className="kids-activity-row kids-activity-row--reversed">
            <div className="kids-activity-image-wrap">
              <img
                src="/images/tideasoliskids/2.jpeg"
                alt={t('kids_space.workshops_section_title')}
                className="kids-activity-img"
                onClick={() => openGallery(2)}
              />
            </div>
            <div className="kids-activity-card">
              <div className="kids-activity-head">
                <span className="kids-activity-emoji">🎨</span>
                <h3 className="kids-activity-title">{t('kids_space.workshops_section_title')}</h3>
              </div>
              <p className="kids-body-text">{t('kids_space.workshops_text')}</p>
            </div>
          </div>

          {/* Row D: Hra & Priestor — text left, image right */}
          <div className="kids-activity-row kids-activity-row--normal">
            <div className="kids-activity-card">
              <div className="kids-activity-head">
                <span className="kids-activity-emoji">🧸</span>
                <h3 className="kids-activity-title">{t('kids_space.play_title')}</h3>
              </div>
              <p className="kids-body-text">{t('kids_space.play_text')}</p>
            </div>
            <div className="kids-activity-image-wrap">
              <img
                src="/images/tideasoliskids/3.jpeg"
                alt={t('kids_space.play_title')}
                className="kids-activity-img"
                onClick={() => openGallery(3)}
              />
            </div>
          </div>

          {/* Row E & F: Tábory + Prenájom — two compact cards side by side */}
          <div className="kids-activity-row kids-activity-row--pair">
            <div className="kids-activity-card kids-activity-card--compact">
              <div className="kids-activity-head">
                <span className="kids-activity-emoji">⛺</span>
                <h3 className="kids-activity-title">{t('kids_space.camps_title')}</h3>
              </div>
              <p className="kids-body-text">{t('kids_space.camps_text')}</p>
            </div>
            <div className="kids-activity-card kids-activity-card--compact">
              <div className="kids-activity-head">
                <span className="kids-activity-emoji">🎈</span>
                <h3 className="kids-activity-title">{t('kids_space.rental_title')}</h3>
              </div>
              <p className="kids-body-text">{t('kids_space.rental_text')}</p>
            </div>
          </div>
        </section>

        {/* ── Section 3: Gallery ───────────────────────────────────────── */}
        <section className="kids-section kids-gallery-section">
          <h2 className="kids-section-title kids-section-title--centered">
            {t('kids_space.gallery_title')}
          </h2>
          <div className="kids-gallery-grid">
            {GALLERY_IMAGES.map((src, index) => (
              <div
                key={index}
                className="kids-gallery-item"
                onClick={() => openGallery(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && openGallery(index)}
                aria-label={`Otvoriť fotografiu ${index + 1}`}
              >
                <img src={src} alt={`TiDea Solis Kids ${index + 1}`} className="kids-gallery-thumb" />
                <div className="kids-gallery-hover">
                  <span className="kids-gallery-zoom">🔍</span>
                </div>
              </div>
            ))}
            {/* Placeholder tile for future photos */}
            <div className="kids-gallery-placeholder" aria-hidden="true">
              <span className="kids-gallery-placeholder-icon">📷</span>
              <span className="kids-gallery-placeholder-text">{t('kids_space.gallery_more_soon')}</span>
            </div>
          </div>
        </section>

        {/* ── Section 4: Reservations ──────────────────────────────────── */}
        <section className="kids-section kids-reservations-section">
          <h2 className="kids-section-title kids-section-title--centered">
            {t('kids_space.reservations_title')}
          </h2>
          <p className="kids-reservation-text">{t('kids_space.reservations_text')}</p>
          <div className="kids-info-items">
            <div className="kids-info-item">
              <span className="kids-info-icon">🌐</span>
              <span>www.tideasolis.com</span>
            </div>
            <div className="kids-info-item">
              <span className="kids-info-icon">📍</span>
              <span>Šafárikova 479/79, Rožňava</span>
            </div>
            <div className="kids-info-item">
              <span className="kids-info-icon">📱</span>
              <a
                href="https://www.instagram.com/tideasolis.kids"
                target="_blank"
                rel="noopener noreferrer"
                className="kids-info-link"
              >
                @tideasolis.kids
              </a>
            </div>
          </div>
          <button className="kids-schedule-btn" onClick={handleViewSchedule}>
            {t('kids_space.view_schedule_btn')}
          </button>
        </section>

        {/* ── Section 5: Partners ──────────────────────────────────────── */}
        <section className="kids-section kids-partners-section">
          <h2 className="kids-section-title kids-section-title--centered">
            {t('kids_space.partners_title')}
          </h2>
          <p className="kids-body-text kids-centered-text">{t('kids_space.partners_text')}</p>
          <div className="kids-partner-logos">
            {KIDS_PARTNERS.map((partner) =>
              partner.url ? (
                <a key={partner.name} href={partner.url} target="_blank" rel="noopener noreferrer" className="kids-partner-logo-link">
                  <div className="kids-partner-logo-wrap">
                    <img src={partner.logo} alt={partner.name} className="kids-partner-logo-img" />
                  </div>
                </a>
              ) : (
                <div key={partner.name} className="kids-partner-logo-link">
                  <div className="kids-partner-logo-wrap">
                    <img src={partner.logo} alt={partner.name} className="kids-partner-logo-img" />
                  </div>
                </div>
              )
            )}
          </div>
          <div className="kids-partner-cta">
            <p className="kids-partner-cta-title">{t('kids_space.partner_cta')}</p>
            <p className="kids-body-text kids-centered-text">{t('kids_space.partner_cta_text')}</p>
            <p className="kids-partner-contact">
              +421 907 510 511 &nbsp;·&nbsp; timikerekesova@gmail.com
            </p>
          </div>
        </section>

        {/* ── Section 6: Rules ─────────────────────────────────────────── */}
        <section className="kids-section kids-rules-section">
          <h2 className="kids-section-title kids-section-title--centered">
            {t('kids_space.rules_title')}
          </h2>
          <p className="kids-body-text kids-centered-text">{t('kids_space.rules_intro')}</p>
          <div className="kids-rules-toggle-wrap">
            <button
              className="kids-rules-toggle-btn"
              onClick={() => setRulesExpanded(!rulesExpanded)}
              aria-expanded={rulesExpanded}
            >
              {rulesExpanded ? t('kids_space.rules_hide') : t('kids_space.rules_show')}
              <span className="kids-expand-arrow">{rulesExpanded ? ' ▲' : ' ▼'}</span>
            </button>
          </div>
          {rulesExpanded && (
            <div className="kids-rules-list kids-fade-in">
              {RULES.map((rule, index) => (
                <div key={index} className="kids-rule-item">
                  <span className="kids-rule-num">{index + 1}.</span>
                  <span className="kids-rule-emoji">{rule.emoji}</span>
                  <span className="kids-rule-text">{t(rule.key)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

      </div>

      {/* ── Lightbox Gallery ─────────────────────────────────────────────── */}
      {galleryOpen && (
        <div
          className="kids-lightbox"
          onClick={closeGallery}
          role="dialog"
          aria-modal="true"
          aria-label="Galéria fotografií"
        >
          <div
            className="kids-lightbox-content"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
            onTouchEnd={(e) => {
              if (touchStartX === null) return;
              const diff = e.changedTouches[0].clientX - touchStartX;
              if (Math.abs(diff) > 50) {
                diff > 0 ? prevImage() : nextImage();
              }
              setTouchStartX(null);
            }}
          >
            <img
              src={GALLERY_IMAGES[galleryIndex]}
              alt={`TiDea Solis Kids ${galleryIndex + 1}`}
              className="kids-lightbox-img"
            />
            <button className="kids-lightbox-close" onClick={closeGallery} aria-label="Zavrieť">
              &times;
            </button>
            {GALLERY_IMAGES.length > 1 && (
              <>
                <button className="kids-lightbox-prev" onClick={prevImage} aria-label="Predchádzajúci">
                  &#8249;
                </button>
                <button className="kids-lightbox-next" onClick={nextImage} aria-label="Nasledujúci">
                  &#8250;
                </button>
                <div className="kids-lightbox-counter">
                  {galleryIndex + 1} / {GALLERY_IMAGES.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KidsSpacePage;
